import React, { useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Marker, useMap } from 'react-leaflet';
import { LatLngExpression } from 'leaflet';
import axios from 'axios';
import TrailDrawer, { MarkerGroup, ALL_MARKER_GROUPS } from '../components/TrailDrawer';
import StatsDrawer from '../components/StatsDrawer';
import EventFooterBar from '../components/EventFooterBar';
import { UnitSystem, ReactionType, ReactionCounts } from '../types/event';
import { formatAltitude, formatSpeed, heartRateToSignalLevel } from '../utils/formatters';
import { TelemetryPoint, mergeTelemetry } from '../utils/telemetryUtils';
import { getFlightThresholds } from '../utils/flightHeuristics';
import { makeRunSplitDivIcon } from '../utils/runHeuristics';
import { useFlightAnalysis } from '../hooks/useFlightAnalysis';
import { useWebSocket } from '../hooks/useWebSocket';

const API_BASE_URL =
  window.__ENV?.REACT_APP_API_URL ||
  process.env.REACT_APP_API_URL ||
  'http://localhost:8000';

interface EventData {
  title: string;
  description: string;
  event_type: string;
  start_time: string | null;
  end_time: string | null;
  event_image_url?: string | null;
  gofundme_link?: string | null;
  gofundme_total?: number | null;
  athlete_name?: string | null;
}

interface FloatingReaction {
  id: string;
  emoji: string;
  leftPct: number;
}

const getOrCreateSpectatorId = (): string => {
  const key = 'spectator_id_v1';
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const id = (globalThis.crypto && 'randomUUID' in globalThis.crypto)
    ? (globalThis.crypto as any).randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  localStorage.setItem(key, id);
  return id;
};

const reactionEmoji = (t: ReactionType) => (t === 'heart' ? '❤️' : '👍');

const MapUpdater: React.FC<{ center: LatLngExpression }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

const EventViewer: React.FC = () => {
  const { hashId } = useParams<{ hashId: string }>();
  const [event, setEvent] = useState<EventData | null>(null);
  const [telemetryPoints, setTelemetryPoints] = useState<TelemetryPoint[]>([]);
  const [currentPosition, setCurrentPosition] = useState<[number, number] | null>(null);
  const [spectatorId] = useState(() => getOrCreateSpectatorId());
  const [reactionCounts, setReactionCounts] = useState<ReactionCounts>({ hearts: 0, thumbs_ups: 0 });
  const [reacted, setReacted] = useState<Record<ReactionType, boolean>>({ heart: false, thumbs_up: false });
  const [floating, setFloating] = useState<FloatingReaction[]>([]);

  // Panel open/closed state.
  // On desktop (≥768 px) panels start expanded; on mobile they start collapsed.
  const [statsOpen, setStatsOpen] = useState(() => window.innerWidth >= 768);
  const [trailOpen, setTrailOpen] = useState(() => window.innerWidth >= 768);

  // Map marker filter state — which movement marker groups are visible.
  const [visibleMarkerGroups, setVisibleMarkerGroups] = useState<Set<MarkerGroup>>(
    () => new Set(ALL_MARKER_GROUPS),
  );

  const handleMarkerGroupToggle = (group: MarkerGroup) => {
    setVisibleMarkerGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  const handleToggleAllMarkers = () => {
    setVisibleMarkerGroups(prev =>
      prev.size === ALL_MARKER_GROUPS.length ? new Set() : new Set(ALL_MARKER_GROUPS),
    );
  };

  const [currentSpeed, setCurrentSpeed] = useState(0);
  const [currentHeartRate, setCurrentHeartRate] = useState<number | null>(null);
  const [currentAltitude, setCurrentAltitude] = useState<number | null>(null);

  const [narrationAvailable, setNarrationAvailable] = useState(false);
  const [narrationEnabled, setNarrationEnabled] = useState(false);
  const [narrationText, setNarrationText] = useState<string | null>(null);
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [eventEnded, setEventEnded] = useState(false);

  // Events older than 24 h past start_time are served read-only; no live WS.
  const isReadOnly = useMemo(() => {
    if (!event?.start_time) return false;
    return Date.now() - new Date(event.start_time).getTime() > 24 * 60 * 60 * 1000;
  }, [event?.start_time]);

  const [unitSystem, setUnitSystem] = useState<UnitSystem>(() => {
    const raw = localStorage.getItem('unit_system_v1');
    if (raw === 'metric' || raw === 'imperial') return raw;
    return 'imperial';
  });

  useEffect(() => {
    localStorage.setItem('unit_system_v1', unitSystem);
  }, [unitSystem]);

  const flightThr = useMemo(() => getFlightThresholds(), []);

  const athleteInitial = useMemo(() => {
    const name = event?.athlete_name?.trim();
    if (name) return name.charAt(0).toUpperCase();
    return event?.event_type === 'flight' ? 'P' : 'A';
  }, [event?.athlete_name, event?.event_type]);

  const {
    verticalSpeedMps,
    flightPhase,
    headingDegNow,
    flightTrailEvents,
    flightMovementMarkers,
    flightColoredPolylines,
    runSplits,
    markerIcon,
    distance,
  } = useFlightAnalysis(telemetryPoints, event?.event_type, flightThr, hashId, unitSystem, athleteInitial);

  const addFloating = (type: ReactionType) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setFloating((prev) => [...prev, { id, emoji: reactionEmoji(type), leftPct: 15 + Math.random() * 70 }]);
    window.setTimeout(() => {
      setFloating((prev) => prev.filter((x) => x.id !== id));
    }, 2600);
  };

  const { capacityMessage, streamActive, disconnected } = useWebSocket(
    hashId,
    !isReadOnly && !eventEnded,
    setTelemetryPoints,
    setCurrentPosition,
    setCurrentSpeed,
    setCurrentAltitude,
    setCurrentHeartRate,
    addFloating,
    setReactionCounts,
    setNarrationText,
    setSpectatorCount,
    () => setEventEnded(true),
  );

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/events/${hashId}`);
        setEvent(response.data);
        if (response.data.end_time) {
          setEventEnded(true);
        }

        const reactionsResponse = await axios.get(`${API_BASE_URL}/events/${hashId}/reactions`);
        setReactionCounts(reactionsResponse.data);

        const telemetryResponse = await axios.get(`${API_BASE_URL}/events/${response.data.id}/telemetry`);
        const points = telemetryResponse.data.points;

        if (points.length > 0) {
          const reconciled = mergeTelemetry([], points);
          setTelemetryPoints(reconciled);
          const lastPoint = reconciled[reconciled.length - 1];
          setCurrentPosition([lastPoint.latitude, lastPoint.longitude]);
          if (typeof lastPoint.altitude === 'number') setCurrentAltitude(lastPoint.altitude);
        }
      } catch (error) {
        console.error('Error fetching event:', error);
      }
    };
    fetchEvent();
  }, [hashId]);

  // Check whether the event creator has stored an OpenAI key (narration available)
  useEffect(() => {
    if (!hashId) return;
    axios.get(`${API_BASE_URL}/events/${hashId}/narration/key/status`)
      .then(res => setNarrationAvailable(res.data.creator_key_stored === true))
      .catch(() => setNarrationAvailable(false));
  }, [hashId]);

  // Poll for narration text every 30 s when the viewer has enabled narration
  useEffect(() => {
    if (!hashId || !narrationEnabled) return;
    const fetchNarration = async () => {
      try {
        const res = await axios.post(`${API_BASE_URL}/events/${hashId}/narration/generate`);
        if (res.data?.text) setNarrationText(res.data.text);
      } catch (e) {
        console.error('Narration generation failed:', e);
      }
    };
    fetchNarration();
    const id = setInterval(fetchNarration, 30_000);
    return () => clearInterval(id);
  }, [hashId, narrationEnabled]);

  useEffect(() => {
    if (!hashId) return;
    const raw = localStorage.getItem(`reacted_v1_${hashId}`);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      setReacted({ heart: Boolean(parsed.heart), thumbs_up: Boolean(parsed.thumbs_up) });
    } catch { /* ignore */ }
  }, [hashId]);

  const persistReacted = (next: Record<ReactionType, boolean>) => {
    if (!hashId) return;
    localStorage.setItem(`reacted_v1_${hashId}`, JSON.stringify(next));
  };

  const sendReaction = async (type: ReactionType) => {
    if (!hashId || reacted[type]) return;
    addFloating(type);
    try {
      const resp = await axios.post(`${API_BASE_URL}/events/${hashId}/reactions`, {
        emoji_type: type,
        viewer_session_id: spectatorId,
      });
      if (resp.data?.counts) setReactionCounts(resp.data.counts);
      const next = { ...reacted, [type]: true } as Record<ReactionType, boolean>;
      setReacted(next);
      persistReacted(next);
    } catch (e) {
      console.error('Error sending reaction:', e);
    }
  };

  if (!event) {
    return (
      <div className="container">
        <div className="header">
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  const defaultCenter: LatLngExpression = currentPosition || [51.505, -0.09];
  const defaultZoom = event.event_type === 'run' ? 16 : 13;
  const pathCoordinates: LatLngExpression[] = telemetryPoints.map(p => [p.latitude, p.longitude]);
  const hasLeftPanel = event.event_type === 'flight' || event.event_type === 'run';

  return (
    <div className="event-viewer">
      <div className="event-top-banner">
        <div className="event-banner-left">
          {event.event_image_url ? (
            <img
              className="event-banner-image"
              src={event.event_image_url}
              alt={event.title}
            />
          ) : (
            <div className="event-banner-title">{event.title}</div>
          )}
        </div>
        <div className="event-banner-right">
          {event.gofundme_link && (
            <div className="donate-group">
              <a
                className="donate-button"
                href={event.gofundme_link}
                target="_blank"
                rel="noreferrer"
              >
                Donate
              </a>
              <div className="donate-total">
                Total: {typeof event.gofundme_total === 'number' ? `$${event.gofundme_total.toLocaleString()}` : '—'}
              </div>
            </div>
          )}
          <div className="wm-mark-box" aria-label="WM">
            <span className="wm-mark-text">WM</span>
          </div>
        </div>
      </div>

      <div className="event-main-area">
        <TrailDrawer
          eventType={event.event_type}
          isOpen={trailOpen}
          flightTrailEvents={flightTrailEvents}
          runSplits={runSplits}
          unitSystem={unitSystem}
          visibleMarkerGroups={visibleMarkerGroups}
          onMarkerGroupToggle={handleMarkerGroupToggle}
          onToggleAllMarkers={handleToggleAllMarkers}
        />

        {/* MAP WRAPPER */}
        <div className="map-wrapper">
          {/* Left drawer toggle */}
          {hasLeftPanel && (
            <button
              className="drawer-toggle drawer-toggle-left"
              onClick={() => setTrailOpen(o => !o)}
              aria-label={trailOpen ? 'Hide trail panel' : 'Show trail panel'}
              title={trailOpen ? 'Hide trail panel' : 'Show trail panel'}
            >
              {trailOpen ? '◀' : '▶'}
            </button>
          )}

          {/* Right drawer toggle */}
          <button
            className="drawer-toggle drawer-toggle-right"
            onClick={() => setStatsOpen(o => !o)}
            aria-label={statsOpen ? 'Hide stats panel' : 'Show stats panel'}
            title={statsOpen ? 'Hide stats panel' : 'Show stats panel'}
          >
            {statsOpen ? '▶' : '◀'}
          </button>

          {/* Narration overlay */}
          {narrationEnabled && narrationText && (
            <div className="narration-overlay">
              <p className="narration-text">{narrationText}</p>
            </div>
          )}

          {/* Floating reaction emojis over the map */}
          <div className="reaction-float-layer">
            {floating.map((r) => (
              <div
                key={r.id}
                className="reaction-float"
                style={{ left: `${r.leftPct}%` }}
              >
                {r.emoji}
              </div>
            ))}
          </div>

          <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            className="map-container"
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {event.event_type === 'flight' ? (
              flightColoredPolylines.length > 0 ? (
                flightColoredPolylines.map((pl) => (
                  <Polyline key={pl.id} positions={pl.positions} color={pl.color} weight={4} opacity={0.85} />
                ))
              ) : (
                pathCoordinates.length > 0 && <Polyline positions={pathCoordinates} color="#2563eb" weight={4} opacity={0.85} />
              )
            ) : (
              pathCoordinates.length > 0 && (
                <Polyline positions={pathCoordinates} color="blue" weight={3} />
              )
            )}

            {event.event_type === 'flight' &&
              flightMovementMarkers
                .filter(m => {
                  const group: MarkerGroup = m.action.startsWith('Turning') ? 'Turning'
                    : m.action === 'Sinking' ? 'Sinking'
                    : 'Climbing';
                  return visibleMarkerGroups.has(group);
                })
                .map((m) => {
                  const titleParts = [m.action];
                  if (m.altM !== null) titleParts.push(`~${formatAltitude(m.altM, unitSystem)}`);
                  if (m.spdKmh !== null) titleParts.push(`~${formatSpeed(m.spdKmh, unitSystem)}`);
                  return <Marker key={m.id} position={m.position} icon={m.icon} title={titleParts.join(' · ')} />;
                })}

            {event.event_type === 'run' &&
              runSplits.map((s) => {
                const hrLevel = s.avgHeartRateBpm !== null ? heartRateToSignalLevel(s.avgHeartRateBpm) : 0;
                const label = `${s.km}k`;
                const title = s.avgHeartRateBpm !== null
                  ? `${label} · ${s.avgHeartRateBpm} bpm`
                  : label;
                return (
                  <Marker
                    key={s.id}
                    position={s.position}
                    icon={makeRunSplitDivIcon(s.km, hrLevel)}
                    title={title}
                  />
                );
              })}

            {currentPosition && (
              <Marker position={currentPosition} icon={markerIcon} />
            )}

            {currentPosition && <MapUpdater center={currentPosition} />}
          </MapContainer>
        </div>

        <StatsDrawer
          eventTitle={event.title}
          eventType={event.event_type}
          isOpen={statsOpen}
          distance={distance}
          currentSpeed={currentSpeed}
          currentAltitude={currentAltitude}
          currentHeartRate={currentHeartRate}
          verticalSpeedMps={isReadOnly ? 0 : verticalSpeedMps}
          flightPhase={isReadOnly ? null : flightPhase}
          headingDegNow={isReadOnly ? null : headingDegNow}
          unitSystem={unitSystem}
          onUnitSystemChange={setUnitSystem}
        />
      </div>

      <EventFooterBar
        isLive={!isReadOnly && !eventEnded && streamActive && telemetryPoints.length > 0}
        isReadOnly={isReadOnly || eventEnded}
        capacityMessage={disconnected ? 'Connection lost. Please refresh the page.' : capacityMessage}
        reactionCounts={reactionCounts}
        reacted={reacted}
        onReact={sendReaction}
        spectatorCount={isReadOnly ? undefined : spectatorCount}
        narrationAvailable={!isReadOnly && narrationAvailable}
        narrationEnabled={narrationEnabled}
        onToggleNarration={() => setNarrationEnabled(e => !e)}
      />
    </div>
  );
};

export default EventViewer;
